import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'
import { momentOffset } from 'utils/time'
import { withStyles } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'

const styles = {
  weekDayNames: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  weekDayName: {
    flex: 1,
  },
  week: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  day: {
    flex: 1,
    border: 0,
    padding: 0,
    margin: 0,
    outline: 0,
    background: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  dayDisabled: {
    cursor: 'default'
  }
}

export default
@withStyles(styles)
class Calendar extends PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    selectedDate: PropTypes.instanceOf(Date),
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    isDayDisabled: PropTypes.func,
    onSelect: PropTypes.func,
    renderWeekDayName: PropTypes.func.isRequired,
    renderTitle: PropTypes.func.isRequired,
    renderDay: PropTypes.func.isRequired,
    utcOffset: PropTypes.number,
  }

  static defaultProps = {
    selectedDate: null,
    minDate: moment().add(-10, 'years').toDate(),
    maxDate: moment().add(10, 'years').toDate(),
    isDayDisabled: () => false,
    onSelect: () => {},
  }

  static getDerivedStateFromProps = ({ selectedDate, utcOffset }) => {
    return {
      selectedDate: selectedDate && momentOffset(utcOffset, selectedDate).startOf('day')
    }
  }

  constructor({ minDate, maxDate, selectedDate, isDayDisabled, utcOffset }) {
    super()
    const startOfSelectedDate = momentOffset(utcOffset, selectedDate).startOf('day')
    const startOfMinDate = momentOffset(utcOffset, minDate).startOf('day')
    const startOfMaxDate = momentOffset(utcOffset, maxDate).startOf('day')

    // Find best start date
    let currentDate
    if (startOfSelectedDate.isValid()) {
      currentDate = startOfSelectedDate.clone()
    } else {
      currentDate = momentOffset(utcOffset)

      // Find first enabled day
      const currentDateIterator = currentDate.clone()
      while (
        isDayDisabled(currentDateIterator.toDate())
        && Math.abs(currentDateIterator.diff(currentDate, 'days')) < 60
      ) {
        currentDateIterator.add(1, 'days')
      }
      currentDate = currentDateIterator
    }

    this.state = {
      currentDate,
      selectedDate: startOfSelectedDate,
      minDate: startOfMinDate,
      maxDate: startOfMaxDate,
    }
  }

  handlePrevClick = () => {
    const { currentDate } = this.state
    this.setState({
      currentDate: currentDate.clone().add(-1, 'months'),
    })
  }

  handleNextClick = () => {
    const { currentDate } = this.state
    this.setState({
      currentDate: currentDate.clone().add(1, 'months'),
    })
  }

  handleDayClick = (ev) => {
    const { onSelect } = this.props
    const dayStart = ev.currentTarget.getAttribute('data-date')
    onSelect(new Date(dayStart))
  }

  renderTitle() {
    const { renderTitle } = this.props
    const { currentDate, minDate, maxDate } = this.state
    const monthStart = currentDate.clone().startOf('month')
    const monthEnd = currentDate.clone().endOf('month')
    const canPrev = !monthStart.isBefore(minDate)
    const canNext = !monthEnd.isAfter(maxDate)

    const prevButton = (
      <IconButton disabled={!canPrev} onClick={this.handlePrevClick}>
        <KeyboardArrowLeftIcon />
      </IconButton>
    )

    const nextButton = (
      <IconButton disabled={!canNext} onClick={this.handleNextClick}>
        <KeyboardArrowRightIcon />
      </IconButton>
    )

    return renderTitle({
      label: monthStart.format('YYYY MMMM'),
      prevButton,
      nextButton
    })
  }

  renderWeekDayNames() {
    const { classes, renderWeekDayName } = this.props
    const { currentDate } = this.state
    const weekStart = currentDate.clone().startOf('isoWeek')
    const children = []
    for (let i = 0; i < 7; i += 1) {
      children.push(
        <div key={i} className={classes.weekDayName}>
          { renderWeekDayName({
            label: weekStart.clone().add(i, 'days').format('ddd')
          }) }
        </div>
      )
    }

    return (
      <div className={classes.weekDayNames}>
        { children }
      </div>
    )
  }

  renderWeeks() {
    const { currentDate } = this.state
    const monthEnd = currentDate.clone().endOf('month')
    const weekStart = currentDate.clone().startOf('month').startOf('isoWeek')
    const children = []
    while (weekStart.isBefore(monthEnd)) {
      children.push(this.renderWeek(weekStart))
      weekStart.add(1, 'weeks')
    }
    return (
      <Fragment>
        { children }
      </Fragment>
    )
  }

  renderWeek(weekStart) {
    const { classes } = this.props
    const weekEnd = weekStart.clone().endOf('isoWeek')
    const children = []
    const dayStart = weekStart.clone().startOf('day')
    while (dayStart.isBefore(weekEnd)) {
      children.push(this.renderDay(dayStart))
      dayStart.add(1, 'days')
    }
    return (
      <div key={weekStart.toISOString()} className={classes.week}>
        { children }
      </div>
    )
  }

  renderDay(dayStart) {
    const { classes, isDayDisabled, renderDay } = this.props
    const { minDate, maxDate, currentDate, selectedDate } = this.state
    const monthStart = currentDate.clone().startOf('month')
    const monthEnd = currentDate.clone().endOf('month')

    const isWithinCurrentMonth = !dayStart.isBefore(monthStart)
      && !dayStart.isAfter(monthEnd)

    const isDisabled = !isWithinCurrentMonth
      || dayStart.isBefore(minDate)
      || dayStart.isAfter(maxDate)
      || isDayDisabled(dayStart.toDate())

    const isSelected = isWithinCurrentMonth
      && dayStart.isSame(selectedDate)

    const dayStartISO = dayStart.toISOString()
    const cx = classNames(classes.day, {
      [classes.dayDisabled]: isDisabled
    })

    return (
      <button
        type="button"
        key={dayStartISO}
        className={cx}
        data-date={dayStartISO}
        tabIndex="-1"
        onClick={!isDisabled && !isSelected ? this.handleDayClick : undefined}
      >
        { isWithinCurrentMonth && renderDay({
          date: dayStart.toDate(),
          isDisabled,
          isSelected
        }) }
      </button>
    )
  }

  render() {
    return (
      <Fragment>
        { this.renderTitle() }
        { this.renderWeekDayNames() }
        { this.renderWeeks() }
      </Fragment>
    )
  }
}
